import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  checkCoordinatorAdmin,
  fetchCoordinatorAdmins,
  addCoordinatorAdmin,
  removeCoordinatorAdmin,
  fetchCoordinatorIcons,
  fetchMapConfig,
  updateMapConfig,
  fetchEditAccess,
  updateEditAccess,
  fetchApiUsage,
  uploadCoordinatorIcon,
  deleteCoordinatorIcon,
  deleteTypeIcon,
  updateIconVisibility,
  uploadDefaultIcon,
  deleteDefaultIcon,
  addSubtypeRule,
  updateSubtypeRule,
  deleteSubtypeRule,
  uploadSubtypeIcon,
  uploadItemIcon,
  deleteItemIcon,
  addItemSubtypeRule,
  updateItemSubtypeRule,
  deleteItemSubtypeRule,
  uploadItemSubtypeIcon,
  updateItemVisibility,
} from '../services/api';
import './CoordinatorMapSettings.css';

export default function CoordinatorMapSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [seedAdmin, setSeedAdmin] = useState('');
  const [iconConfig, setIconConfig] = useState({ defaultIconUrl: null, items: [], types: [] });
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [clusterMaxZoom, setClusterMaxZoom] = useState(14);
  const [savedClusterMaxZoom, setSavedClusterMaxZoom] = useState(14);
  const [repRefreshHours, setRepRefreshHours] = useState(4);
  const [savedRepRefreshHours, setSavedRepRefreshHours] = useState(4);
  const [ticketCacheMinutes, setTicketCacheMinutes] = useState(5);
  const [savedTicketCacheMinutes, setSavedTicketCacheMinutes] = useState(5);
  const [messageCacheMinutes, setMessageCacheMinutes] = useState(5);
  const [savedMessageCacheMinutes, setSavedMessageCacheMinutes] = useState(5);
  const [ticketLabelField, setTicketLabelField] = useState('siteName');
  const [savedTicketLabelField, setSavedTicketLabelField] = useState('siteName');
  const [liveLocationEnabled, setLiveLocationEnabled] = useState(false);
  const [savingLiveLocation, setSavingLiveLocation] = useState(false);
  const [labelBackground, setLabelBackground] = useState(true);
  const [savingLabelBackground, setSavingLabelBackground] = useState(false);
  const [apiUsage, setApiUsage] = useState([]);
  const [editAccessMode, setEditAccessMode] = useState('allowlist');
  const [editAllowlist, setEditAllowlist] = useState([]);
  const [newEditEmail, setNewEditEmail] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    async function init() {
      try {
        const adminCheck = await checkCoordinatorAdmin();
        if (!adminCheck.isAdmin) {
          navigate('/coordinator-map');
          return;
        }
        setIsAdmin(true);

        const [adminResult, iconResult, mapConfigResult, usageResult, editAccessResult] = await Promise.all([
          fetchCoordinatorAdmins(),
          fetchCoordinatorIcons(),
          fetchMapConfig().catch(() => ({ clusterMaxZoom: 14 })),
          fetchApiUsage().catch(() => ({ usage: [] })),
          fetchEditAccess().catch(() => ({ mode: 'allowlist', allowlist: [] })),
        ]);

        setAdmins(adminResult.admins || []);
        setSeedAdmin(adminResult.seedAdmin || '');
        setIconConfig(iconResult);
        setClusterMaxZoom(mapConfigResult.clusterMaxZoom ?? 14);
        setSavedClusterMaxZoom(mapConfigResult.clusterMaxZoom ?? 14);
        setRepRefreshHours(mapConfigResult.repRefreshHours ?? 4);
        setSavedRepRefreshHours(mapConfigResult.repRefreshHours ?? 4);
        setTicketCacheMinutes(mapConfigResult.ticketCacheMinutes ?? 5);
        setSavedTicketCacheMinutes(mapConfigResult.ticketCacheMinutes ?? 5);
        setMessageCacheMinutes(mapConfigResult.messageCacheMinutes ?? 5);
        setSavedMessageCacheMinutes(mapConfigResult.messageCacheMinutes ?? 5);
        setTicketLabelField(mapConfigResult.ticketLabelField ?? 'siteName');
        setSavedTicketLabelField(mapConfigResult.ticketLabelField ?? 'siteName');
        setLiveLocationEnabled(!!mapConfigResult.liveLocationEnabled);
        setLabelBackground(mapConfigResult.labelBackground ?? true);
        setApiUsage(usageResult.usage || []);
        setEditAccessMode(editAccessResult.mode || 'allowlist');
        setEditAllowlist(editAccessResult.allowlist || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [navigate]);

  // Group types by item name
  const itemGroups = useMemo(() => {
    const items = iconConfig.items || [];
    const types = iconConfig.types || [];

    // Build map of itemSlug → { item, types[] }
    const groups = new Map();

    for (const item of items) {
      groups.set(item.slug, { item, types: [] });
    }

    for (const type of types) {
      const itemSlug = type.itemSlug;
      if (itemSlug && groups.has(itemSlug)) {
        groups.get(itemSlug).types.push(type);
      } else {
        // Orphan type — create a placeholder group
        const key = itemSlug || `_orphan_${type.slug}`;
        if (!groups.has(key)) {
          groups.set(key, { item: { slug: key, name: itemSlug || type.slug, iconUrl: null, visible: true }, types: [] });
        }
        groups.get(key).types.push(type);
      }
    }

    // Sort groups by item name, then types within each group
    return [...groups.values()]
      .sort((a, b) => (a.item.name || '').localeCompare(b.item.name || ''))
      .map(g => ({ ...g, types: g.types.sort((a, b) => (a.name || '').localeCompare(b.name || '')) }));
  }, [iconConfig]);

  // Admin handlers
  const handleAddAdmin = useCallback(async () => {
    if (!newAdminEmail.trim()) return;
    try {
      const result = await addCoordinatorAdmin(newAdminEmail.trim());
      setAdmins(result.admins);
      setNewAdminEmail('');
    } catch (err) { alert(err.message); }
  }, [newAdminEmail]);

  const handleRemoveAdmin = useCallback(async (email) => {
    if (!window.confirm(`Remove ${email} as admin?`)) return;
    try {
      const result = await removeCoordinatorAdmin(email);
      setAdmins(result.admins);
    } catch (err) { alert(err.message); }
  }, []);

  // Edit access handlers
  const handleEditModeChange = useCallback(async (mode) => {
    try {
      const result = await updateEditAccess({ mode });
      setEditAccessMode(result.mode);
      setEditAllowlist(result.allowlist || []);
    } catch (err) { alert(err.message); }
  }, []);

  const handleAddEditUser = useCallback(async () => {
    const email = newEditEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (editAllowlist.includes(email)) {
      setNewEditEmail('');
      return;
    }
    try {
      const result = await updateEditAccess({ allowlist: [...editAllowlist, email] });
      setEditAllowlist(result.allowlist || []);
      setNewEditEmail('');
    } catch (err) { alert(err.message); }
  }, [newEditEmail, editAllowlist]);

  const handleRemoveEditUser = useCallback(async (email) => {
    if (!window.confirm(`Remove ${email} from ticket-editing access?`)) return;
    try {
      const result = await updateEditAccess({ allowlist: editAllowlist.filter(e => e !== email) });
      setEditAllowlist(result.allowlist || []);
    } catch (err) { alert(err.message); }
  }, [editAllowlist]);

  // Default icon handlers
  const handleUploadDefault = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadDefaultIcon(file);
      setIconConfig(prev => ({ ...prev, defaultIconUrl: result.defaultIconUrl }));
    } catch (err) { alert(err.message); }
    e.target.value = '';
  }, []);

  const handleDeleteDefault = useCallback(async () => {
    try {
      await deleteDefaultIcon();
      setIconConfig(prev => ({ ...prev, defaultIconUrl: null }));
    } catch (err) { alert(err.message); }
  }, []);

  // Item icon handlers
  const handleUploadItemIcon = useCallback(async (slug, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadItemIcon(slug, file);
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => i.slug === slug ? { ...i, iconUrl: result.iconUrl } : i)
      }));
    } catch (err) { alert(err.message); }
    e.target.value = '';
  }, []);

  const handleDeleteItemIcon = useCallback(async (slug) => {
    try {
      await deleteItemIcon(slug);
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => i.slug === slug ? { ...i, iconUrl: null } : i)
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleToggleItemVisibility = useCallback(async (slug, visible) => {
    try {
      await updateItemVisibility(slug, visible);
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => i.slug === slug ? { ...i, visible } : i)
      }));
    } catch (err) { alert(err.message); }
  }, []);

  // Item subtype rule handlers
  const handleAddItemSubtype = useCallback(async (slug) => {
    try {
      const rule = await addItemSubtypeRule(slug, {
        field: 'description', condition: 'contains', value: '', visible: true,
      });
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => {
          if (i.slug !== slug) return i;
          return { ...i, subtypes: [...(i.subtypes || []), rule] };
        })
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleUpdateItemSubtype = useCallback(async (slug, subtypeId, updates) => {
    try {
      await updateItemSubtypeRule(slug, subtypeId, updates);
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => {
          if (i.slug !== slug) return i;
          return { ...i, subtypes: (i.subtypes || []).map(s => s.id === subtypeId ? { ...s, ...updates } : s) };
        })
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleDeleteItemSubtype = useCallback(async (slug, subtypeId) => {
    try {
      await deleteItemSubtypeRule(slug, subtypeId);
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => {
          if (i.slug !== slug) return i;
          return { ...i, subtypes: (i.subtypes || []).filter(s => s.id !== subtypeId) };
        })
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleUploadItemSubtypeIcon = useCallback(async (slug, subtypeId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadItemSubtypeIcon(slug, subtypeId, file);
      setIconConfig(prev => ({
        ...prev,
        items: prev.items.map(i => {
          if (i.slug !== slug) return i;
          return { ...i, subtypes: (i.subtypes || []).map(s => s.id === subtypeId ? { ...s, iconUrl: result.iconUrl } : s) };
        })
      }));
    } catch (err) { alert(err.message); }
    e.target.value = '';
  }, []);

  // Type icon handlers
  const handleUploadTypeIcon = useCallback(async (slug, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadCoordinatorIcon(slug, file);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => t.slug === slug ? { ...t, iconUrl: result.iconUrl } : t)
      }));
    } catch (err) { alert(err.message); }
    e.target.value = '';
  }, []);

  const handleDeleteType = useCallback(async (slug) => {
    if (!window.confirm(`Delete type "${slug}" and all its icons?`)) return;
    try {
      await deleteCoordinatorIcon(slug);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.filter(t => t.slug !== slug)
      }));
    } catch (err) { alert(err.message); }
  }, []);

  // Clear a type's override icon without deleting the type — reverts to the inherited item icon
  const handleDeleteTypeIcon = useCallback(async (slug) => {
    try {
      await deleteTypeIcon(slug);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => t.slug === slug ? { ...t, iconUrl: null } : t)
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleToggleVisibility = useCallback(async (slug, visible) => {
    try {
      await updateIconVisibility(slug, visible);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => t.slug === slug ? { ...t, visible } : t)
      }));
    } catch (err) { alert(err.message); }
  }, []);

  // Subtype handlers
  const handleAddSubtype = useCallback(async (slug) => {
    try {
      const rule = await addSubtypeRule(slug, {
        field: 'description', condition: 'contains', value: '', visible: true,
      });
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => {
          if (t.slug !== slug) return t;
          return { ...t, subtypes: [...(t.subtypes || []), rule] };
        })
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleUpdateSubtype = useCallback(async (slug, subtypeId, updates) => {
    try {
      await updateSubtypeRule(slug, subtypeId, updates);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => {
          if (t.slug !== slug) return t;
          return { ...t, subtypes: (t.subtypes || []).map(s => s.id === subtypeId ? { ...s, ...updates } : s) };
        })
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleDeleteSubtype = useCallback(async (slug, subtypeId) => {
    try {
      await deleteSubtypeRule(slug, subtypeId);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => {
          if (t.slug !== slug) return t;
          return { ...t, subtypes: (t.subtypes || []).filter(s => s.id !== subtypeId) };
        })
      }));
    } catch (err) { alert(err.message); }
  }, []);

  const handleUploadSubtypeIcon = useCallback(async (slug, subtypeId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadSubtypeIcon(slug, subtypeId, file);
      setIconConfig(prev => ({
        ...prev,
        types: prev.types.map(t => {
          if (t.slug !== slug) return t;
          return { ...t, subtypes: (t.subtypes || []).map(s => s.id === subtypeId ? { ...s, iconUrl: result.iconUrl } : s) };
        })
      }));
    } catch (err) { alert(err.message); }
    e.target.value = '';
  }, []);

  if (loading) return <div className="settings-page"><div className="settings-loading">Loading settings...</div></div>;
  if (!isAdmin) return null;

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <Link to="/coordinator-map" className="settings-back">&larr; Back to Map</Link>
          <h1>Coordinator Map Settings</h1>
        </div>

        {error && <div className="settings-error">{error}</div>}

        {/* Admin Management */}
        <section className="settings-section">
          <h2>Admin Users</h2>
          <p className="section-desc">Manage who can modify map icons and settings.</p>
          <div className="admin-list">
            {admins.map(email => (
              <div key={email} className="admin-row">
                <span>{email}</span>
                {email === seedAdmin ? (
                  <span className="admin-badge">Primary</span>
                ) : (
                  <button className="remove-btn" onClick={() => handleRemoveAdmin(email)}>Remove</button>
                )}
              </div>
            ))}
          </div>
          <div className="add-admin-row">
            <input
              type="email"
              placeholder="email@siteservicesnow.com"
              value={newAdminEmail}
              onChange={e => setNewAdminEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddAdmin()}
            />
            <button onClick={handleAddAdmin}>Add Admin</button>
          </div>
        </section>

        {/* Ticket Editing Access */}
        <section className="settings-section">
          <h2>Ticket Editing Access</h2>
          <p className="section-desc">
            Controls who can edit ticket fields directly from the map. Admins can always edit.
          </p>
          <div className="edit-access-mode">
            <label>
              <input
                type="radio"
                name="editMode"
                checked={editAccessMode === 'allowlist'}
                onChange={() => handleEditModeChange('allowlist')}
              />
              <span><strong>Allowlist</strong> — only admins and the emails below can edit</span>
            </label>
            <label>
              <input
                type="radio"
                name="editMode"
                checked={editAccessMode === 'all'}
                onChange={() => handleEditModeChange('all')}
              />
              <span><strong>All Users</strong> — every signed-in coordinator can edit</span>
            </label>
          </div>

          {editAccessMode === 'allowlist' && (
            <>
              <div className="admin-list" style={{ marginTop: 12 }}>
                {editAllowlist.length === 0 ? (
                  <p className="empty-state">No additional users — admins only.</p>
                ) : (
                  editAllowlist.map(email => (
                    <div key={email} className="admin-row">
                      <span>{email}</span>
                      <button className="remove-btn" onClick={() => handleRemoveEditUser(email)}>Remove</button>
                    </div>
                  ))
                )}
              </div>
              <div className="add-admin-row">
                <input
                  type="email"
                  placeholder="email@siteservicesnow.com"
                  value={newEditEmail}
                  onChange={e => setNewEditEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddEditUser()}
                />
                <button onClick={handleAddEditUser}>Add Editor</button>
              </div>
            </>
          )}
        </section>

        {/* QuickBase API Usage */}
        <section className="settings-section">
          <h2>QuickBase API Usage</h2>
          <p className="section-desc">API calls tracked over the last 7 days.</p>
          {apiUsage.length === 0 ? (
            <p className="empty-state">No API usage recorded yet.</p>
          ) : (
            <>
              <div className="usage-summary">
                {apiUsage.map(day => (
                  <div key={day.date} className="usage-day">
                    <span className="usage-date">{day.date}</span>
                    <span className="usage-stat">{day.totalCalls} calls</span>
                    <span className="usage-stat">{day.totalRecords?.toLocaleString()} records</span>
                  </div>
                ))}
              </div>
              {apiUsage[0]?.calls?.length > 0 && (
                <RecentCalls calls={apiUsage[0].calls} />
              )}
            </>
          )}
        </section>

        {/* Map Display Settings */}
        <section className="settings-section">
          <h2>Map Display</h2>
          <div className="cluster-zoom-row">
            <label className="filter-label">Cluster Max Zoom Level</label>
            <p className="section-desc" style={{ margin: '0 0 8px' }}>
              Markers cluster below this zoom level. Higher = clusters persist when more zoomed in. (1-20)
            </p>
            <div className="cluster-zoom-control">
              <input
                type="range"
                min={1}
                max={20}
                value={clusterMaxZoom}
                onChange={e => setClusterMaxZoom(Number(e.target.value))}
              />
              <span className="cluster-zoom-value">{clusterMaxZoom}</span>
              <button
                className="upload-btn"
                disabled={clusterMaxZoom === savedClusterMaxZoom}
                onClick={async () => {
                  try {
                    await updateMapConfig({ clusterMaxZoom });
                    setSavedClusterMaxZoom(clusterMaxZoom);
                  } catch (err) { alert(err.message); }
                }}
              >
                Save
              </button>
            </div>
          </div>
          <div className="cluster-zoom-row" style={{ marginTop: 16 }}>
            <label className="filter-label">Field Rep Refresh Rate</label>
            <p className="section-desc" style={{ margin: '0 0 8px' }}>
              How often field rep data is refreshed from QuickBase. (2-24 hours)
            </p>
            <div className="cluster-zoom-control">
              <input
                type="range"
                min={2}
                max={24}
                value={repRefreshHours}
                onChange={e => setRepRefreshHours(Number(e.target.value))}
              />
              <span className="cluster-zoom-value">{repRefreshHours}h</span>
              <button
                className="upload-btn"
                disabled={repRefreshHours === savedRepRefreshHours}
                onClick={async () => {
                  try {
                    await updateMapConfig({ repRefreshHours });
                    setSavedRepRefreshHours(repRefreshHours);
                  } catch (err) { alert(err.message); }
                }}
              >
                Save
              </button>
            </div>
          </div>
          <div className="cluster-zoom-row" style={{ marginTop: 16 }}>
            <label className="filter-label">Ticket Cache Duration</label>
            <p className="section-desc" style={{ margin: '0 0 8px' }}>
              How long ticket data is cached before re-querying QuickBase. Refresh button always bypasses cache. (1-30 minutes)
            </p>
            <div className="cluster-zoom-control">
              <input
                type="range"
                min={1}
                max={30}
                value={ticketCacheMinutes}
                onChange={e => setTicketCacheMinutes(Number(e.target.value))}
              />
              <span className="cluster-zoom-value">{ticketCacheMinutes}m</span>
              <button
                className="upload-btn"
                disabled={ticketCacheMinutes === savedTicketCacheMinutes}
                onClick={async () => {
                  try {
                    await updateMapConfig({ ticketCacheMinutes });
                    setSavedTicketCacheMinutes(ticketCacheMinutes);
                  } catch (err) { alert(err.message); }
                }}
              >
                Save
              </button>
            </div>
          </div>
          <div className="cluster-zoom-row" style={{ marginTop: 16 }}>
            <label className="filter-label">Message Cache Duration</label>
            <p className="section-desc" style={{ margin: '0 0 8px' }}>
              How long open-message counts are cached before re-querying QuickBase. Open maps pick up a refresh from the same status poll as tickets and reps. (1-30 minutes)
            </p>
            <div className="cluster-zoom-control">
              <input
                type="range"
                min={1}
                max={30}
                value={messageCacheMinutes}
                onChange={e => setMessageCacheMinutes(Number(e.target.value))}
              />
              <span className="cluster-zoom-value">{messageCacheMinutes}m</span>
              <button
                className="upload-btn"
                disabled={messageCacheMinutes === savedMessageCacheMinutes}
                onClick={async () => {
                  try {
                    await updateMapConfig({ messageCacheMinutes });
                    setSavedMessageCacheMinutes(messageCacheMinutes);
                  } catch (err) { alert(err.message); }
                }}
              >
                Save
              </button>
            </div>
          </div>
          <div className="cluster-zoom-row" style={{ marginTop: 16 }}>
            <label className="filter-label">Marker Label Field</label>
            <p className="section-desc" style={{ margin: '0 0 8px' }}>
              Org default for the ticket field shown next to markers. Users can override this in Layers on the map.
            </p>
            <div className="cluster-zoom-control">
              <select
                value={ticketLabelField}
                onChange={e => setTicketLabelField(e.target.value)}
                style={{ flex: 1, padding: '4px 6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }}
              >
                <option value="siteName">Site Name</option>
                <option value="recordId">Record ID</option>
                <option value="projectName">Project Name</option>
                <option value="serviceType">Service Type</option>
                <option value="itemName">Item Name</option>
                <option value="status">Status</option>
                <option value="fieldRepName">Field Rep Name</option>
                <option value="city">City</option>
                <option value="state">State</option>
                <option value="dueDate">Due Date</option>
                <option value="scheduleDate">Schedule Date</option>
                <option value="startDate">Start Date</option>
              </select>
              <button
                className="upload-btn"
                disabled={ticketLabelField === savedTicketLabelField}
                onClick={async () => {
                  try {
                    await updateMapConfig({ ticketLabelField });
                    setSavedTicketLabelField(ticketLabelField);
                  } catch (err) { alert(err.message); }
                }}
              >
                Save
              </button>
            </div>
            <label className="layer-toggle" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <input
                type="checkbox"
                checked={labelBackground}
                disabled={savingLabelBackground}
                onChange={async e => {
                  const next = e.target.checked;
                  setLabelBackground(next);
                  setSavingLabelBackground(true);
                  try {
                    await updateMapConfig({ labelBackground: next });
                  } catch (err) {
                    setLabelBackground(!next); // revert on failure
                    alert(err.message);
                  } finally {
                    setSavingLabelBackground(false);
                  }
                }}
              />
              Label background pill (improves readability over busy areas)
            </label>
          </div>
          <div className="cluster-zoom-row" style={{ marginTop: 16 }}>
            <label className="filter-label">Live Rep Locations</label>
            <p className="section-desc" style={{ margin: '0 0 8px' }}>
              Show the live location layer fed by the tech portal. When off, the layer is
              hidden and the map stops fetching positions (incoming pings are still stored).
            </p>
            <label className="layer-toggle" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={liveLocationEnabled}
                disabled={savingLiveLocation}
                onChange={async e => {
                  const next = e.target.checked;
                  setLiveLocationEnabled(next);
                  setSavingLiveLocation(true);
                  try {
                    await updateMapConfig({ liveLocationEnabled: next });
                  } catch (err) {
                    setLiveLocationEnabled(!next); // revert on failure
                    alert(err.message);
                  } finally {
                    setSavingLiveLocation(false);
                  }
                }}
              />
              {liveLocationEnabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </section>

        {/* Default Icon */}
        <section className="settings-section">
          <h2>Default Icon</h2>
          <p className="section-desc">Fallback icon for items without a specific icon.</p>
          <div className="default-icon-row">
            {iconConfig.defaultIconUrl ? (
              <>
                <img src={iconConfig.defaultIconUrl} alt="Default icon" className="icon-preview" />
                <button className="remove-btn" onClick={handleDeleteDefault}>Remove</button>
              </>
            ) : (
              <span className="no-icon">No default icon set</span>
            )}
            <label className="upload-btn">
              Upload PNG
              <input type="file" accept="image/png" onChange={handleUploadDefault} hidden />
            </label>
          </div>
        </section>

        {/* Item Groups */}
        <section className="settings-section">
          <h2>Icons by Item Name</h2>
          <p className="section-desc">
            Set an icon at the item name level to apply to all types with that item. Individual types can override.
          </p>
          {itemGroups.length === 0 ? (
            <p className="empty-state">No items registered yet. Load the map to auto-register.</p>
          ) : (
            <div className="type-list">
              {itemGroups.map(({ item, types }) => (
                <ItemGroup
                  key={item.slug}
                  item={item}
                  types={types}
                  onUploadItemIcon={handleUploadItemIcon}
                  onDeleteItemIcon={handleDeleteItemIcon}
                  onToggleItemVisibility={handleToggleItemVisibility}
                  onAddItemSubtype={handleAddItemSubtype}
                  onUpdateItemSubtype={handleUpdateItemSubtype}
                  onDeleteItemSubtype={handleDeleteItemSubtype}
                  onUploadItemSubtypeIcon={handleUploadItemSubtypeIcon}
                  onUploadTypeIcon={handleUploadTypeIcon}
                  onDeleteType={handleDeleteType}
                  onDeleteTypeIcon={handleDeleteTypeIcon}
                  onToggleVisibility={handleToggleVisibility}
                  onAddSubtype={handleAddSubtype}
                  onUpdateSubtype={handleUpdateSubtype}
                  onDeleteSubtype={handleDeleteSubtype}
                  onUploadSubtypeIcon={handleUploadSubtypeIcon}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function RecentCalls({ calls }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="usage-recent">
      <h4 onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
        {expanded ? '▾' : '▸'} Recent Calls (Today) — {calls.length}
      </h4>
      {expanded && (
        <div className="usage-call-list">
          {calls.slice().reverse().slice(0, 15).map((call, i) => (
            <div key={i} className="usage-call">
              <span className="call-method">{call.method}</span>
              <span className="call-table">{call.table}</span>
              <span className="call-records">{call.records?.toLocaleString()} rec</span>
              <span className="call-duration">{call.durationMs}ms</span>
              <span className="call-trigger">{call.trigger || '—'}</span>
              <span className="call-time">{new Date(call.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemGroup({
  item, types,
  onUploadItemIcon, onDeleteItemIcon, onToggleItemVisibility,
  onAddItemSubtype, onUpdateItemSubtype, onDeleteItemSubtype, onUploadItemSubtypeIcon,
  onUploadTypeIcon, onDeleteType, onDeleteTypeIcon, onToggleVisibility,
  onAddSubtype, onUpdateSubtype, onDeleteSubtype, onUploadSubtypeIcon,
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="item-group">
      <div className="item-header">
        <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? '▾' : '▸'}
        </button>
        {item.iconUrl ? (
          <img src={item.iconUrl} alt="" className="icon-preview-sm" />
        ) : (
          <span className="no-icon-sm">--</span>
        )}
        <span className="item-name">{item.name || item.slug}</span>
        <span className="item-count">{types.length} type{types.length !== 1 ? 's' : ''}</span>
        <label className="visibility-toggle">
          <input
            type="checkbox"
            checked={item.visible !== false}
            onChange={e => onToggleItemVisibility(item.slug, e.target.checked)}
          />
          Visible
        </label>
        <label className="upload-btn-sm">
          Icon
          <input type="file" accept="image/png" onChange={e => onUploadItemIcon(item.slug, e)} hidden />
        </label>
        {item.iconUrl && (
          <button className="remove-btn-sm" onClick={() => onDeleteItemIcon(item.slug)}>Remove Icon</button>
        )}
      </div>

      {expanded && (
        <div className="item-types">
          {/* Item-level subtype rules */}
          <div className="type-subtypes" style={{ paddingLeft: 8 }}>
            <div className="subtypes-header">
              <span>Subtype Rules (apply to all types in this group)</span>
              <button className="add-subtype-btn" onClick={() => onAddItemSubtype(item.slug)}>+ Add Rule</button>
            </div>
            {(item.subtypes || []).map(sub => (
              <SubtypeRow
                key={sub.id}
                slug={item.slug}
                subtype={sub}
                onUpdate={onUpdateItemSubtype}
                onDelete={onDeleteItemSubtype}
                onUploadIcon={onUploadItemSubtypeIcon}
              />
            ))}
            {(!item.subtypes || item.subtypes.length === 0) && (
              <p className="empty-subtypes">No subtype rules — all tickets use the item icon</p>
            )}
          </div>

          {/* Individual types */}
          {types.length === 0 ? (
            <p className="empty-subtypes">No specific types registered</p>
          ) : (
            types.map(type => (
              <TypeRow
                key={type.slug}
                type={type}
                parentItemIcon={item.iconUrl}
                onUploadIcon={onUploadTypeIcon}
                onDeleteType={onDeleteType}
                onDeleteTypeIcon={onDeleteTypeIcon}
                onToggleVisibility={onToggleVisibility}
                onAddSubtype={onAddSubtype}
                onUpdateSubtype={onUpdateSubtype}
                onDeleteSubtype={onDeleteSubtype}
                onUploadSubtypeIcon={onUploadSubtypeIcon}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TypeRow({
  type, parentItemIcon,
  onUploadIcon, onDeleteType, onDeleteTypeIcon, onToggleVisibility,
  onAddSubtype, onUpdateSubtype, onDeleteSubtype, onUploadSubtypeIcon,
}) {
  const [expanded, setExpanded] = useState(false);
  const effectiveIcon = type.iconUrl || parentItemIcon;

  return (
    <div className="type-row">
      <div className="type-header">
        <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? '▾' : '▸'}
        </button>
        {effectiveIcon ? (
          <img src={effectiveIcon} alt="" className="icon-preview-sm" style={!type.iconUrl ? { opacity: 0.4 } : {}} />
        ) : (
          <span className="no-icon-sm">--</span>
        )}
        <span className="type-name" title={type.slug}>{type.name || type.slug}</span>
        {!type.iconUrl && parentItemIcon && <span className="inherited-badge">inherited</span>}
        <label className="visibility-toggle">
          <input
            type="checkbox"
            checked={type.visible !== false}
            onChange={e => onToggleVisibility(type.slug, e.target.checked)}
          />
          Visible
        </label>
        <label className="upload-btn-sm">
          Override
          <input type="file" accept="image/png" onChange={e => onUploadIcon(type.slug, e)} hidden />
        </label>
        {type.iconUrl && (
          <button className="remove-btn-sm" onClick={() => onDeleteTypeIcon(type.slug)}>Clear Override</button>
        )}
      </div>

      {expanded && (
        <div className="type-subtypes">
          <div className="subtypes-header">
            <span>Subtype Rules</span>
            <button className="add-subtype-btn" onClick={() => onAddSubtype(type.slug)}>+ Add Rule</button>
          </div>
          {(type.subtypes || []).map(sub => (
            <SubtypeRow
              key={sub.id}
              slug={type.slug}
              subtype={sub}
              onUpdate={onUpdateSubtype}
              onDelete={onDeleteSubtype}
              onUploadIcon={onUploadSubtypeIcon}
            />
          ))}
          {(!type.subtypes || type.subtypes.length === 0) && (
            <p className="empty-subtypes">No subtype rules</p>
          )}
        </div>
      )}
    </div>
  );
}

const SUBTYPE_FIELDS = ['description', 'serviceType', 'itemName', 'projectName', 'siteName', 'status'];
const SUBTYPE_CONDITIONS = ['contains', 'not_contains', 'equals', 'not_equals'];

function SubtypeRow({ slug, subtype, onUpdate, onDelete, onUploadIcon }) {
  const [localField, setLocalField] = useState(subtype.field);
  const [localCondition, setLocalCondition] = useState(subtype.condition);
  const [localValue, setLocalValue] = useState(subtype.value || '');
  const [saved, setSaved] = useState(false);
  const hasChanges = localField !== subtype.field || localCondition !== subtype.condition || localValue !== (subtype.value || '');

  const handleSave = async () => {
    await onUpdate(slug, subtype.id, { field: localField, condition: localCondition, value: localValue });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="subtype-row">
      <select value={localField} onChange={e => setLocalField(e.target.value)}>
        {SUBTYPE_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
      </select>
      <select value={localCondition} onChange={e => setLocalCondition(e.target.value)}>
        {SUBTYPE_CONDITIONS.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
      </select>
      <input
        type="text"
        value={localValue}
        placeholder="match value"
        onChange={e => setLocalValue(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && hasChanges && handleSave()}
      />
      <button className="upload-btn-sm" disabled={!hasChanges && !saved} onClick={handleSave}>
        {saved ? '✓ Saved' : 'Save'}
      </button>
      {subtype.iconUrl && <img src={subtype.iconUrl} alt="" className="icon-preview-sm" />}
      <label className="upload-btn-sm">
        Icon
        <input type="file" accept="image/png" onChange={e => onUploadIcon(slug, subtype.id, e)} hidden />
      </label>
      <label className="visibility-toggle-sm">
        <input
          type="checkbox"
          checked={subtype.visible !== false}
          onChange={e => onUpdate(slug, subtype.id, { visible: e.target.checked })}
        />
      </label>
      <button className="remove-btn-sm" onClick={() => onDelete(slug, subtype.id)}>x</button>
    </div>
  );
}
